import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminPageHeader from '../../../components/admin/AdminPageHeader';
// ServicePage.tsx — Co Marca Admin — estilo AboutusProjects (igual que CategoryPage)
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Edit, Trash2, Plus, History, Store, MapPin, Check, X, Search } from 'lucide-react';
import { fetchServicios } from '../../../services/servicioService';
import AddServiceModal from '../../../components/Service/AddServiceModal';
import EditServiceModal from '../../../components/Service/EditServiceModal';
import DeleteServiceModal from '../../../components/Service/DeleteServiceModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `


.srv-page { font-family: 'DM Sans', sans-serif; }

@keyframes sectionFadeUp {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
}

.srv-fadeup   { animation: sectionFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.srv-fadeup-1 { animation: sectionFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }

.srv-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:9px 20px; font-size:.83rem; font-weight:800;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);
    white-space:nowrap;
}
.srv-btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32); }

.srv-btn-secondary {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-surface, #fff); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:9px 16px; font-size:.83rem; font-weight:700;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .2s;
}
.srv-btn-secondary:hover { background:var(--ad-soft, #f8fafc); border-color:color-mix(in srgb, ${colors.secondary} 25.1%, transparent); color:var(--ad-link, #2854c5); }

.srv-table-row { transition:background .15s; }
.srv-table-row:hover { background:var(--ad-soft, #fafcff); }

.srv-action-btn {
    width:30px; height:30px; border-radius:9px; border:none;
    display:inline-flex; align-items:center; justify-content:center;
    cursor:pointer; transition:all .2s;
}

.custom-scroll::-webkit-scrollbar { height:6px; width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color:rgba(97, 37, 123,0.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color:rgba(97, 37, 123,0.5); }
`;

const ServicePage: React.FC = () => {
    const [servicios, setServicios]               = useState<any[]>([]);
    const [showAddModal, setShowAddModal]         = useState(false);
    const [showEditModal, setShowEditModal]       = useState(false);
    const [showDeleteModal, setShowDeleteModal]   = useState(false);
    const [selectedService, setSelectedService]   = useState<any>(null);
    const [currentPage, setCurrentPage]           = useState(1);
    const itemsPerPage = 10;
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('servicio', userLevelId);
    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl]       = useState<string | null>(null);

    const loadServicios = useCallback(async () => {
        try {
            if (perm?.can_read) {
                const data = await fetchServicios();
                setServicios(data.sort((a: any, b: any) => a.id - b.id));
            } else {
                setServicios([]);
            }
        } catch (error) {
            /* Browser diagnostics omitted. */
            setServicios([]);
        }
    }, [perm]);

    useEffect(() => { loadServicios(); }, [loadServicios]);

    const filteredServices = useMemo(() => {
        const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const query = normalize(searchTerm.trim());
        return servicios.filter(service => normalize(`${service.nombre || ''} ${service.categoria_nombre || ''} ${service.descripcion || ''}`).includes(query));
    }, [servicios, searchTerm]);
    useEffect(() => { setCurrentPage(1); }, [searchTerm]);
    const totalPages = Math.max(1, Math.ceil(filteredServices.length / itemsPerPage));
    useEffect(() => { setCurrentPage(page => Math.min(page, totalPages)); }, [totalPages]);
    const startIndex        = (currentPage - 1) * itemsPerPage;
    const displayedServices = filteredServices.slice(startIndex, startIndex + itemsPerPage);

    const handleImagePreview = (url: string) => { setPreviewImageUrl(url); setIsImagePreviewOpen(true); };
    const handleEditClick    = (s: any) => { setSelectedService(s); setShowEditModal(true); };
    const handleDeleteClick  = (s: any) => { setSelectedService(s); setShowDeleteModal(true); };

    if (perm === null) return <Loading text="Cargando servicios..." />;

    return <><LumaResourceWorkspace title="Servicios" description="Lista completa de servicios" group="Servicios" rows={servicios} columns={[{key:'nombre',label:'Nombre'},{key:'categoria_nombre',label:'Categoría'},{key:'tipo_servicio',label:'Tipo de servicio',render:r=>r.tipo_servicio==='oficina_virtual'?'Oficina virtual':'Orden de servicio'},{key:'estado',label:'Estado'},{key:'codigo_producto_sin',label:'Producto / servicio SIN',render:r=>r.codigo_producto_sin||r.producto_sin_nombre||'Sin asignar'}]} idKey="id" nameKey="nombre" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setShowAddModal(true)}:undefined} onEdit={perm?.can_update?handleEditClick:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history="/services/history"/><AddServiceModal    isOpen={showAddModal}    onClose={()=>setShowAddModal(false)}    onServiceAdded={loadServicios}/><EditServiceModal   isOpen={showEditModal}   onClose={()=>setShowEditModal(false)}   onServiceUpdated={loadServicios} servicio={selectedService}/><DeleteServiceModal isOpen={showDeleteModal} onClose={()=>setShowDeleteModal(false)} servicio={selectedService} onDeleteConfirmed={loadServicios}/><ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/></>;
};

export default ServicePage;
